/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-non-null-assertion */
import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { 
  ReactFlow, 
  useNodesState, 
  useEdgesState, 
  useReactFlow, 
  Node, 
  ReactFlowProvider
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Server, ChevronLeft, ChevronRight } from 'lucide-react';

// Data and Hooks
import {
  SelectedEntity,
  CloudInventory,
  BgpNeighborData,
  BgpNeighborRoster,
  VpcData,
  CloudData,
  EntityStatusInfo,
  useBgpVisibilityData
} from './BgpVisibilityData';

// Components
import CloudInventorySidebar from './CloudInventory/CloudInventorySideBar';
import { useAuth0 } from '@auth0/auth0-react';
import { useSelector } from 'react-redux';
import IPv4InfoModal from './BGPModals/IPv4InfoModal';
import VpcModal from './BGPModals/VpcModal';
import RouteTableModal from './BGPModals/RouteTableModal';
import { getCloudInventorySidebar, getBgpVisibilityNeighborData, getBgpVisibilityRoutes } from '../Reflector/request/request';
import { fetchCprDataFromApi, extractCprDataForClusterReflector, getRawClusterResponse, getEntityStatusInfo, extractAllClusterMemberCprs, formatClusterRole, isClusterMemberUp, memberMatchesSelection, pickPreferredClusterMember, ClusterMemberCpr } from './helpers/bgpVisibilityApi';
import { createRequestOwnership } from './helpers/requestOwnership';
import { ErrorStateView, EmptyStateView } from 'kontrolEdge/components/Backbone/BackboneDetails/StateViews';

// Extracted Components
import { VisibilityToolbar } from './components/GraphControls';
import { SideNavTitlePortal } from '@shared/navigation/SideNav/SideNavTitleSlot';
import { ClusterSelector } from './components/ClusterSelector';
import { ReflectorDownOverlay } from './components/ReflectorDownOverlay';
import LicenseGate from '../shared/LicenseGate';
import { useLicenseGate } from '../shared/useLicenseGate';
import { deriveBgpSessionState, deriveDownNeighborsWithoutRoutes, rosterSessionNeighbors } from '../../helpers/bgpValidation';
import { GraphLegend } from './components/GraphLegend';
import { BgpTableView } from './components/BgpTableView';
import { NeighborDetailPanel } from './components/NeighborDetailPanel';
import { OffsetBezierEdge } from './components/OffsetBezierEdge';
import { createNodeTypes } from './utils/nodeTypes';
import { generateGraphNodesAndEdges } from './utils/graphGeneration';
import { buildRouteRows, computeRouteMetrics, countDistinctPrefixes, mapWireRouteRows, RouteRow, WireRouteRow } from './utils/routeModel';
import { RouteFilters, DEFAULT_ROUTE_FILTERS, applyRouteFilters, applyClientOnlyRouteFilters, toServerRouteFilters } from './utils/filterModel';
import { bgpVisibilityStyles } from './styles/bgpVisibilityStyles';

/**
 * How long a filter keystroke settles before the Table re-requests `/visibility/routes`.
 * Matches `GenericSearchbar`'s server-side default, so the two search surfaces feel the same.
 */
const SERVER_FILTER_DEBOUNCE_MS = 600;

/** How a cluster member is named in prose: "Primary CTXR-5cb248", or just the name without a role. */
const clusterMemberLabel = (role: ClusterMemberCpr['role'], name: string): string => {
  const label = formatClusterRole(role);
  return label ? `${label} ${name}` : name;
};

/** CTA copy naming the healthy member by role ("View secondary data"), or by name when roleless. */
const viewMemberCtaLabel = (role: ClusterMemberCpr['role'], name: string): string => {
  const label = formatClusterRole(role);
  return label ? `View ${label.toLowerCase()} data` : `View ${name} data`;
};

const BgpVisibilityPage: React.FC = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const { fitView } = useReactFlow();
  const { getAccessTokenSilently } = useAuth0();
  const tenantId = useSelector((state: any) => state.selection.tenantId);

  // Suppress ResizeObserver errors
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      if (args[0]?.includes?.('ResizeObserver loop completed with undelivered notifications')) {
        return;
      }
      originalError(...args);
    };

    return () => {
      console.error = originalError;
    };
  }, []);

  
  // State
  const [selectedEntity, setSelectedEntity] = useState<SelectedEntity | null>(null);
  const [currentCpr, setCurrentCpr] = useState<any>(null);

  // BGP Visibility addresses reflectors by `subId` (its documents live in S3 under
  // `${tenantId}/${subId}/...`), while clusters already carry their numeric id — so the two
  // entity kinds resolve through different scopes (CTX-346).
  const licenseGate = useLicenseGate(
    selectedEntity?.type === 'cluster'
      ? { scope: 'cluster', id: selectedEntity.id }
      : { scope: 'instanceBySubId', id: selectedEntity?.subId || selectedEntity?.id || null }
  );
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entityStatusInfo, setEntityStatusInfo] = useState<EntityStatusInfo | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [neighborData, setNeighborData] = useState<BgpNeighborRoster>({});
  
  const selectedEntityRef = useRef<SelectedEntity | null>(null);
  const effectiveEntityRef = useRef<SelectedEntity | null>(null);
  const currentCprRef = useRef<any>(null);
  const isFetchingRef = useRef<boolean>(false);
  const lastTenantIdRef = useRef<number | null>(null);

  /**
   * Ownership of the two per-entity requests (CTX-276). Selecting another CTXR supersedes whatever
   * is in flight, so a response belonging to a no-longer-selected reflector is discarded instead
   * of landing on the current one's canvas. Replaces the old "skip if a fetch is in flight" guard,
   * which dropped the *new* selection's fetch and stranded the page on the previous result.
   */
  const cprOwnership = useRef(createRequestOwnership()).current;
  const neighborOwnership = useRef(createRequestOwnership()).current;
  const routesOwnership = useRef(createRequestOwnership()).current;
  
  // Direction and Family controls
  const [direction, setDirection] = useState<'INBOUND' | 'OUTBOUND'>('INBOUND');
  const [bestOnly, setBestOnly] = useState(false);

  // Topology vs Table view switch (shares filters + metrics)
  const [viewMode, setViewMode] = useState<'topology' | 'table'>('topology');

  // Left Cloud Inventory sidebar collapse (more room for the topology).
  const [leftSidebarCollapsed, setLeftSidebarCollapsed] = useState(false);

  // Shared search/filter state, applied consistently to topology, table, and metrics.
  const [routeFilters, setRouteFilters] = useState<RouteFilters>(DEFAULT_ROUTE_FILTERS);

  // All cluster members' CPRs (Table mode "all members" scope). Empty for reflector mode.
  const [clusterMemberCprs, setClusterMemberCprs] = useState<ClusterMemberCpr[]>([]);

  /**
   * Table rows as `/visibility/routes` serves them, already narrowed by the filters the
   * endpoint owns (CTX-602). `null` means "no server rows to use": the first paint before
   * the response lands, a failed request, and permanently in fixture builds — the table
   * then falls back to the rows derived from the `/visibility` document, which is what
   * Topology reads anyway.
   */
  const [serverRouteRows, setServerRouteRows] = useState<RouteRow[] | null>(null);

  // Cluster reflector selection (when a cluster is selected)
  const [selectedClusterReflector, setSelectedClusterReflector] = useState<string | null>(null);
  // True while the current cluster-member selection is the auto-bootstrapped one (role/status
  // preferred). A deliberate user tab switch clears it so the PRIMARY-down fallback re-pick
  // never yanks the user off a member they explicitly chose. Reset per cluster.
  const autoSelectedClusterMemberRef = useRef(true);

  // Docked neighbor-detail panel: the IP of the neighbor whose prefixes are shown (null = closed).
  const [selectedNeighborIp, setSelectedNeighborIp] = useState<string | null>(null);

  // Modal state
  const [ipv4InfoModal, setIpv4InfoModal] = useState<{ isOpen: boolean; prefixData: any | null; neighborIp: string | null; neighborInfo?: { peerAs?: number; description?: string; operatingMode?: string; status?: string; statusChangedAt?: number } }>({
    isOpen: false,
    prefixData: null,
    neighborIp: null,
    neighborInfo: undefined
  });
  const [vpcModal, setVpcModal] = useState<{ isOpen: boolean; data: VpcData | null }>({
    isOpen: false,
    data: null
  });
  const [routeTableModal, setRouteTableModal] = useState<{ isOpen: boolean; routeTableData: any | null; vpcId: string | null }>({
    isOpen: false,
    routeTableData: null,
    vpcId: null
  });

  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [cloudInventory, setCloudInventory] = useState<CloudInventory | null>(null);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  // Use custom hooks for data management
  const { mapSSEDataToCloudInventory } = useBgpVisibilityData();

  // Get cluster reflectors from cloudInventory
  const getClusterReflectors = useCallback((clusterId: string): Array<{ id: string; display_name: string }> => {
    if (!cloudInventory) {
      return [];
    }
    
    // Flatten all clusters from all clouds, accounts, and regions
    const allClusters = cloudInventory.clouds
      .flatMap(cloud => cloud.accounts
        .flatMap(account => account.regions
          .flatMap(region => region.clusters || [])));
    
    // Find the target cluster
    const cluster = allClusters.find(c => c.id === clusterId);
    
    if (!cluster) {
      return [];
    }
    
    // Extract and map reflectors
    if (!Array.isArray(cluster.reflectors)) {
      return [];
    }
    
    return cluster.reflectors.map((r: any) => ({
      id: r.id || r,
      display_name: r.name || r.display_name || r
    }));
  }, [cloudInventory]);

  // Get current effective entity (reflector if cluster is selected, otherwise selectedEntity)
  const getEffectiveEntity = useCallback((): SelectedEntity | null => {
    if (!selectedEntity) return null;
    
    if (selectedEntity.type === 'cluster' && selectedClusterReflector) {
      // Return the selected reflector within the cluster. subId must be overridden too —
      // otherwise the spread below leaves the CLUSTER's own subId in place, and callers that
      // prefer `subId` over `id` (e.g. fetchNeighborData) end up querying with the cluster's
      // identity instead of the selected member's, silently returning no neighbor data (AS/etc).
      return {
        ...selectedEntity,
        type: 'reflector',
        id: selectedClusterReflector,
        subId: selectedClusterReflector,
        display_name: getClusterReflectors(selectedEntity.id).find(r => r.id === selectedClusterReflector)?.display_name || selectedClusterReflector
      };
    }
    
    return selectedEntity;
  }, [selectedEntity, selectedClusterReflector, getClusterReflectors]);


  // A deliberate cluster-member tab switch: pins the selection so the role-aware re-pick below
  // won't override it.
  const handleClusterReflectorChange = useCallback((reflectorId: string) => {
    autoSelectedClusterMemberRef.current = false;
    setSelectedClusterReflector(reflectorId);
  }, []);

  // Cluster member selection: bootstrap + PRIMARY-down auto-fallback (CTX-71).
  useEffect(() => {
    if (selectedEntity?.type !== 'cluster') {
      // Clear selection when switching away from cluster.
      setSelectedClusterReflector(null);
      autoSelectedClusterMemberRef.current = true;
      return;
    }

    const reflectors = getClusterReflectors(selectedEntity.id);
    if (reflectors.length === 0) {
      return;
    }

    const currentIsValid = selectedClusterReflector && reflectors.some(r => r.id === selectedClusterReflector);

    // No valid selection yet (new cluster / stale selection) → bootstrap to the role-preferred
    // member. `clusterMemberCprs` may still be empty here; the helper then deterministically
    // picks the first reflector and the re-pick below corrects it once member data arrives.
    if (!currentIsValid) {
      const preferred = pickPreferredClusterMember(reflectors, clusterMemberCprs) ?? reflectors[0].id;
      setSelectedClusterReflector(preferred);
      autoSelectedClusterMemberRef.current = true;
      return;
    }

    // Member data (role + up/down) has loaded and we're still on the auto-picked member: if the
    // PRIMARY is down (or a better member is now up), fall back to the preferred one. Skipped once
    // the user has deliberately switched tabs.
    if (autoSelectedClusterMemberRef.current && clusterMemberCprs.length > 0) {
      const preferred = pickPreferredClusterMember(reflectors, clusterMemberCprs);
      if (preferred && preferred !== selectedClusterReflector) {
        setSelectedClusterReflector(preferred);
      }
    }
  }, [selectedEntity, getClusterReflectors, selectedClusterReflector, clusterMemberCprs]);

  // Get authentication token
  useEffect(() => {
    const getToken = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        setToken(accessToken);
      } catch (error) {
        console.error('Error getting access token:', error);
      }
    };
    getToken();
  }, [getAccessTokenSilently]);

  // Load static cloud inventory as fallback only (only if API fails)
  const loadStaticCloudInventory = useCallback(async () => {
    try {
      const response = await fetch('/data/tenants/9/cloud_inventory.json');
      if (response.ok) {
        const data = await response.json();
        const mappedData = mapSSEDataToCloudInventory(data);
        if (mappedData) {
          setCloudInventory(mappedData);
        setInitialDataLoaded(true);
        setLastUpdate(new Date());
        }
      }
    } catch (error) {
      console.error('[Fallback] Failed to load static cloud inventory:', error);
    }
  }, [mapSSEDataToCloudInventory]);



  // Load CPR data function - fetch from API when entity is selected
  const loadCprData = useCallback(async () => {
    const effectiveEntity = getEffectiveEntity();

    if (!effectiveEntity || !token || !tenantId) {
      setCurrentCpr(null);
      return;
    }

    // Supersede whatever is still in flight: the newest selection always wins (CTX-276).
    const { signal, isCurrent: ownsPageState } = cprOwnership.claim();

    setLoading(true);
    setError(null);

    try {
      // Convert direction from 'INBOUND'/'OUTBOUND' to 'inbound'/'outbound'
      const apiDirection = direction.toLowerCase() as 'inbound' | 'outbound';

      // For cluster mode: always fetch fresh from /visibility (CTX-98). The previous in-memory
      // cache made Refresh (and direction/member switches) serve a stale cached array instead of
      // re-hitting the endpoint.
      if (selectedEntity?.type === 'cluster') {
        // Bridge the member id-spaces: selectedClusterReflector is the sidebar subId, but the
        // /visibility response may key members by name (older backend). Pass both the subId and
        // the resolved display name so the extractor matches whichever the payload uses.
        const memberSelection = selectedClusterReflector
          ? { id: selectedClusterReflector, name: effectiveEntity?.display_name ?? null }
          : null;

        // Fetch all reflectors' data in one call.
        // Use subId for clusters (fallback to id if subId not available)
        const clusterId = selectedEntity.subId || selectedEntity.id;
        const rawResponse = await getRawClusterResponse(token, clusterId, apiDirection, tenantId, signal);
        if (!ownsPageState()) {
          return;
        }

        if (rawResponse && Array.isArray(rawResponse)) {
          // Extract the selected reflector's data
          const cprData = extractCprDataForClusterReflector(rawResponse, memberSelection);
          setEntityStatusInfo(getEntityStatusInfo(rawResponse, memberSelection));
          setClusterMemberCprs(extractAllClusterMemberCprs(rawResponse));
          setCurrentCpr(cprData);
          if (cprData) {
            setLastUpdate(new Date());
          }
        } else {
          setEntityStatusInfo(null);
          setClusterMemberCprs([]);
          setCurrentCpr(null);
        }
      } else {
        // Reflector mode: fetch directly (no caching needed)
        const { cpr, statusInfo } = await fetchCprDataFromApi(
          token,
          effectiveEntity,
          apiDirection,
          tenantId,
          null,
          signal
        );

        if (!ownsPageState()) {
          return;
        }

        setEntityStatusInfo(statusInfo);
        setClusterMemberCprs([]);
        setCurrentCpr(cpr);
        if (cpr) {
          setLastUpdate(new Date());
        }
      }
    } catch (err) {
      // A superseded request (including the abort we triggered ourselves) must not surface an
      // error over the selection that replaced it.
      if (!ownsPageState()) {
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load CPR data');
      setEntityStatusInfo(null);
      setCurrentCpr(null);
    } finally {
      // Only the newest request may clear the spinner — otherwise an outgoing request settling
      // late would report the current selection as loaded while its data is still in flight.
      if (ownsPageState()) {
        setLoading(false);
      }
    }
  }, [getEffectiveEntity, token, direction, selectedEntity, selectedClusterReflector, tenantId]);

  /**
   * Loads the Table's rows from `/visibility/routes` (CTX-602).
   *
   * Runs alongside `loadCprData` rather than replacing it: Topology, the cluster selector and
   * the CTX-125 liveness split all read the `/visibility` document, and this endpoint serves
   * routes only. The request is deliberately scoped to the ENTITY, not to the selected cluster
   * member — Table mode is the all-members view, with a Reflector column to tell them apart.
   *
   * A failure leaves `serverRouteRows` null rather than surfacing an error: the document-derived
   * rows still render the table, so the endpoint degrades instead of emptying the view.
   */
  const loadRouteRows = useCallback(async (serverFilters: Record<string, string>) => {
    if (!selectedEntity || !token || !tenantId) {
      setServerRouteRows(null);
      return;
    }

    // Supersede whatever is still in flight, on the same rule as the document fetch (CTX-276).
    const { signal, isCurrent: ownsRows } = routesOwnership.claim();
    const apiDirection = direction.toLowerCase() as 'inbound' | 'outbound';
    const entityId = selectedEntity.subId || selectedEntity.id;

    try {
      const response = await getBgpVisibilityRoutes(
        token,
        selectedEntity.type,
        entityId,
        apiDirection,
        tenantId,
        serverFilters,
        signal
      );
      if (!ownsRows()) {
        return;
      }
      setServerRouteRows(response ? mapWireRouteRows(response.items as WireRouteRow[]) : null);
    } catch {
      if (ownsRows()) {
        setServerRouteRows(null);
      }
    }
  }, [selectedEntity, token, tenantId, direction, routesOwnership]);

  /**
   * The filters the endpoint applies itself.
   *
   * Keyed on the SERIALIZED query rather than on `routeFilters`, so the effect below re-requests
   * only when the query the endpoint will actually see changes. Memoizing on the filter object
   * instead would re-fetch on every `tag` / `search` keystroke — filters this side owns, whose
   * whole point is that they cost no round trip.
   */
  const serverRouteFilterKey = JSON.stringify(toServerRouteFilters(routeFilters, bestOnly));
  const serverRouteFilters = useMemo(
    () => JSON.parse(serverRouteFilterKey) as Record<string, string>,
    [serverRouteFilterKey]
  );

  // A keystroke in Prefix / Neighbor / AS / Local-Pref changes the query string, so it is a new
  // request. Debounced at the cadence the shared searchbar uses for its own server-side mode, so
  // typing narrows the table without firing a request per character.
  useEffect(() => {
    const timer = setTimeout(() => loadRouteRows(serverRouteFilters), SERVER_FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [loadRouteRows, serverRouteFilters]);

  // Fetch neighbor data for status display
  const fetchNeighborData = useCallback(async () => {
    const effectiveEntity = getEffectiveEntity();
    
    if (!effectiveEntity || !token || !tenantId) {
      setNeighborData({});
      return;
    }

    // Same ownership rule as the CPR document (CTX-276): the roster must never describe a
    // different reflector than the one whose graph is on screen.
    const { signal, isCurrent: ownsNeighborData } = neighborOwnership.claim();

    try {
      // Use subId for both reflectors and clusters (fallback to id if subId not available)
      const nameOrId = effectiveEntity.subId || effectiveEntity.id;
      const entityType = effectiveEntity.type;

      const response = await getBgpVisibilityNeighborData(token, entityType, nameOrId, tenantId, signal);

      if (!ownsNeighborData()) {
        return;
      }

      if (response.data && typeof response.data === 'object') {
        // Response is an object with neighbor IPs as keys
        setNeighborData(response.data);
      } else {
        setNeighborData({});
      }
    } catch (err) {
      if (!ownsNeighborData()) {
        return;
      }
      console.error('[BGP Visibility] Error fetching neighbor data:', err);
      setNeighborData({});
    }
  }, [getEffectiveEntity, token, tenantId]);

  // Fetch neighbor data when entity changes
  useEffect(() => {
    fetchNeighborData();
  }, [fetchNeighborData]);

  // Load cloud inventory from API
  const fetchCloudInventory = useCallback(async () => {
    if (!token || !tenantId) {
      // If no token or tenantId, try static file as fallback
      if (!initialDataLoaded) {
        await loadStaticCloudInventory();
      }
      return;
    }
    
    // Prevent duplicate calls
    if (isFetchingRef.current) {
      return;
    }
    
    isFetchingRef.current = true;
    setIsFetching(true);
    setLoading(true);
    try {
      const response = await getCloudInventorySidebar(token, tenantId);
      const data = response.data;
      
      // Map the response data using the same mapping function
      const mappedData = mapSSEDataToCloudInventory(data);
      if (mappedData) {
        // API data always takes precedence - override any static data
        setCloudInventory(mappedData);
        setInitialDataLoaded(true);
        setLastUpdate(new Date());
      } else {
        // Fallback to static file if API mapping fails
        if (!initialDataLoaded) {
          await loadStaticCloudInventory();
        }
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to load cloud inventory from API');
      // Fallback to static file if API fails
      if (!initialDataLoaded) {
        await loadStaticCloudInventory();
      }
    } finally {
      setLoading(false);
      setIsFetching(false);
      isFetchingRef.current = false;
    }
  }, [token, tenantId, mapSSEDataToCloudInventory, initialDataLoaded, loadStaticCloudInventory]);

  // Load cloud inventory from API when token and tenantId are available (primary data source)
  // Also refetch when tenantId changes
  useEffect(() => {
    // Check if tenantId has changed
    const tenantIdChanged = lastTenantIdRef.current !== null && lastTenantIdRef.current !== tenantId;
    
    if (tenantIdChanged) {
      // Tenant changed - reset state and clear cache to force refetch
      setInitialDataLoaded(false);
      setCloudInventory(null);
      setSelectedEntity(null);
      setCurrentCpr(null);
      setEntityStatusInfo(null);
      setSelectedNeighborIp(null);
      lastTenantIdRef.current = tenantId;
    }
    
    if (token && tenantId && !isFetchingRef.current) {
      // Fetch if we haven't loaded data yet, or if tenantId changed
      if (!initialDataLoaded || tenantIdChanged) {
        fetchCloudInventory();
        lastTenantIdRef.current = tenantId;
      }
    } else if ((!token || !tenantId) && !initialDataLoaded) {
      // If no token or tenantId yet, wait a bit then try static file as fallback
      const timeout = setTimeout(() => {
        if (!initialDataLoaded) {
          loadStaticCloudInventory();
        }
      }, 2000); // Wait 2 seconds for token/tenantId, then fallback
      
      return () => clearTimeout(timeout);
    }
    // Only depend on token and tenantId to prevent duplicate calls when other dependencies change
    // The ref guards (isFetchingRef) prevent duplicate calls
  }, [token, tenantId, fetchCloudInventory, loadStaticCloudInventory, initialDataLoaded]);


  // URL state management
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cloud = params.get('cloud');
    const account_id = params.get('account');
    const region = params.get('region');
    const type = params.get('type');
    const id = params.get('id');
    
    if (cloud && account_id && region && type && id) {
      setSelectedEntity({
        cloud,
        account_id,
        account_display_name: account_id, // Default fallback
        region,
        type: type as 'reflector' | 'cluster',
        id,
        display_name: id
      });
    }
  }, []);

  // Update refs when state changes
  useEffect(() => {
    selectedEntityRef.current = selectedEntity;
  }, [selectedEntity]);
  
  useEffect(() => {
    effectiveEntityRef.current = getEffectiveEntity();
  }, [getEffectiveEntity]);
  
  useEffect(() => {
    currentCprRef.current = currentCpr;
  }, [currentCpr]);


  useEffect(() => {
    if (selectedEntity) {
      const params = new URLSearchParams();
      params.set('cloud', selectedEntity.cloud);
      params.set('account', selectedEntity.account_id);
      params.set('region', selectedEntity.region);
      params.set('type', selectedEntity.type);
      params.set('id', selectedEntity.id);
      
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [selectedEntity]);

  // Track last fetch parameters to prevent duplicate calls
  const lastFetchParamsRef = useRef<string>('');

  // Load CPR data when entity is selected, direction changes, or cluster reflector selection changes
  useEffect(() => {
    const effectiveEntity = getEffectiveEntity();
    if (!effectiveEntity || !token || !tenantId) {
      setLoading(false);
      setCurrentCpr(null);
      setEntityStatusInfo(null);
      lastFetchParamsRef.current = '';
      return;
    }

    // Create a unique key for this fetch request
    // Use subId for both reflectors and clusters (fallback to id if subId not available)
    const entityId = effectiveEntity.subId || effectiveEntity.id;
    const fetchKey = `${effectiveEntity.type}-${entityId}-${direction}-${selectedClusterReflector || ''}`;

    // These params have already been requested; the effect re-running (e.g. because `loadCprData`
    // changed identity) is not a reason to hit the endpoint again. Manual Refresh calls
    // `loadCprData` directly, so it is unaffected by this guard.
    //
    // The key is recorded only once the request is actually issued below — recording a fetch that
    // never ran is what previously stranded the page on the old reflector's data (CTX-276).
    if (lastFetchParamsRef.current === fetchKey) {
      return;
    }

    lastFetchParamsRef.current = fetchKey;
    loadCprData();
  }, [direction, selectedEntity?.id, selectedEntity?.type, selectedClusterReflector, token, tenantId, loadCprData, getEffectiveEntity]);

  // Drop the previous selection's document as soon as the selection changes, so the canvas falls
  // back to its loading/empty state instead of presenting the old reflector's graph as the newly
  // selected one's while the request is in flight (CTX-276).
  //
  // Deliberately a layout effect: a passive effect runs *after* paint, which would show one frame
  // of the previous reflector's graph already labelled with the new selection.
  useLayoutEffect(() => {
    setCurrentCpr(null);
    setEntityStatusInfo(null);
  }, [selectedEntity?.id, selectedClusterReflector]);

  // The member roster describes the cluster as a whole, so it only goes stale when the root entity
  // changes — switching between members of the same cluster keeps it (and the selector's status
  // pills) intact.
  useLayoutEffect(() => {
    setClusterMemberCprs([]);
    setNeighborData({});
  }, [selectedEntity?.id]);

  // Nothing in flight may outlive the page.
  useEffect(() => () => {
    cprOwnership.abortPending();
    neighborOwnership.abortPending();
  }, [cprOwnership, neighborOwnership]);

  // Map neighbor IP -> ASN from the neighbor-data endpoint, for the shared route model.
  const neighborAsByIp = useMemo(() => {
    const map: Record<string, number | undefined> = {};
    Object.entries(neighborData).forEach(([ip, info]) => {
      map[ip] = info?.peerAs;
    });
    return map;
  }, [neighborData]);

  // Single liveness split for the whole page (CTX-125). Everything that must not present a
  // down member's data as live — the table rows, the Reflector filter options, the topology
  // overlay and the CTX-106 inconsistency comparison — reads from this one derivation, so the
  // ClusterSelector pill can never disagree with what the surfaces show. "Down" is exactly
  // `isClusterMemberUp`'s rule: offline OR carrying no document.
  const upMembers = useMemo(() => clusterMemberCprs.filter(isClusterMemberUp), [clusterMemberCprs]);
  const downMembers = useMemo(() => clusterMemberCprs.filter(m => !isClusterMemberUp(m)), [clusterMemberCprs]);

  // Every UP cluster member's rows (cluster) or the single member (reflector). Feeds the TABLE
  // view, which is intentionally an all-members view with a Reflector column + filter.
  // A member reporting `offline` while still carrying a stale document is excluded here, so its
  // prefixes never reach the table, the metrics, or the inconsistency comparison.
  const allMemberRows = useMemo(() => {
    if (selectedEntity?.type === 'cluster' && clusterMemberCprs.length > 0) {
      const clusterId = selectedEntity.id;
      const clusterName = selectedEntity.display_name;
      return clusterMemberCprs.filter(isClusterMemberUp).flatMap(member =>
        buildRouteRows({
          cpr: member.cpr,
          direction,
          reflectorId: member.reflectorId,
          reflectorName: member.reflectorName,
          reflectorRole: member.role,
          clusterId,
          clusterName,
          neighborAsByIp
        })
      );
    }
    const effective = getEffectiveEntity();
    return buildRouteRows({
      cpr: currentCpr,
      direction,
      reflectorId: effective?.id || '',
      reflectorName: effective?.display_name || '',
      clusterId: null,
      clusterName: null,
      neighborAsByIp
    });
  }, [selectedEntity, clusterMemberCprs, currentCpr, direction, getEffectiveEntity, neighborAsByIp]);

  /**
   * Cluster member facts the endpoint's rows cannot carry, keyed by BOTH the member's subId and
   * its name: a row names its reflector by `reflector_id` (the subId), while `/visibility`'s
   * member list keys members by subId on current backends and by name on older ones — the same
   * id-space split `memberMatchesSelection` bridges for the selector.
   */
  const clusterMemberByRowKey = useMemo(() => {
    const byKey = new Map<string, ClusterMemberCpr>();
    for (const member of clusterMemberCprs) {
      if (member.reflectorId) {
        byKey.set(member.reflectorId, member);
      }
      if (member.reflectorName) {
        byKey.set(member.reflectorName, member);
      }
    }
    return byKey;
  }, [clusterMemberCprs]);

  /**
   * The endpoint's rows, finished with what only this page knows: the announcing member's HA role
   * and the cluster's identity, which the wire has no column for and the Reflector column renders.
   *
   * Deliberately keeps DOWN members' rows. The liveness rule is not a property of the data, it is
   * a property of each surface: the Table must never present a down member's stale prefixes as
   * live (CTX-125), while Topology deliberately draws that same stale graph behind the overlay so
   * the operator sees the shape of what was there. Filtering here would make the second case
   * impossible to serve from this source.
   */
  const serverEntityRows = useMemo(() => {
    if (!serverRouteRows) {
      return null;
    }
    if (selectedEntity?.type !== 'cluster') {
      return serverRouteRows;
    }
    const clusterName = selectedEntity.display_name || null;
    return serverRouteRows.map(row => ({
      ...row,
      reflectorRole: clusterMemberByRowKey.get(row.reflectorId)?.role ?? null,
      clusterId: selectedEntity.id,
      clusterName
    }));
  }, [serverRouteRows, selectedEntity, clusterMemberByRowKey]);

  /**
   * Whether a row's announcing member is reporting. `getVisibilityRoutes` iterates every cluster
   * instance with no availability filter, so this is the only place the CTX-125 rule is applied
   * to the endpoint's rows — and it is exact rather than approximate *because* the table
   * paginates client-side: nothing downstream reads the server's `pagination.total`, so no count
   * is left to disagree with the rows on screen. A member the document fetch has not described
   * yet counts as up: absence of evidence is not evidence of a down member.
   */
  const rowMemberIsUp = useCallback((row: RouteRow) => {
    const member = clusterMemberByRowKey.get(row.reflectorId);
    return !member || isClusterMemberUp(member);
  }, [clusterMemberByRowKey]);

  /** True while the surfaces are rendering the endpoint's rows rather than the document's. */
  const usingServerRows = serverEntityRows !== null;

  /**
   * Applies whatever filtering the rows still need.
   *
   * Server rows arrive with every filter the endpoint owns already applied, leaving only `tag`
   * and `search`; document rows need the whole set. Stated once, here, and used by every surface
   * — otherwise Table, Topology and the CTX-106 comparison could end up filtering the same query
   * differently depending on which one was edited last.
   */
  const applyActiveFilters = useCallback(
    (rows: RouteRow[]) => (usingServerRows
      ? applyClientOnlyRouteFilters(rows, routeFilters)
      : applyRouteFilters(rows, routeFilters, bestOnly)),
    [usingServerRows, routeFilters, bestOnly]
  );

  const tableRows = serverEntityRows ? serverEntityRows.filter(rowMemberIsUp) : allMemberRows;

  // The `RouteRow.reflectorId` of the member shown in the selector, resolved across the
  // subId/name id-spaces (see memberMatchesSelection). Undefined outside cluster mode.
  const selectedTopologyReflectorId = useMemo(() => {
    if (selectedEntity?.type !== 'cluster' || !selectedClusterReflector) {
      return undefined;
    }
    const name = getClusterReflectors(selectedEntity.id).find(r => r.id === selectedClusterReflector)?.display_name ?? null;
    const match = clusterMemberCprs.find(m =>
      memberMatchesSelection({ id: m.reflectorId, name: m.reflectorName }, { id: selectedClusterReflector, name })
    );
    return match?.reflectorId;
  }, [selectedEntity, selectedClusterReflector, clusterMemberCprs, getClusterReflectors]);

  // The `/visibility` member currently shown in the selector, and whether it is down (CTX-125).
  // Note this is resolved against the full member list, not `upMembers` — a down member is still
  // a legitimate selection; it is the data that gets withheld, not the tab.
  const selectedMember = useMemo(() => {
    if (selectedEntity?.type !== 'cluster' || !selectedClusterReflector) {
      return undefined;
    }
    const name = getClusterReflectors(selectedEntity.id).find(r => r.id === selectedClusterReflector)?.display_name ?? null;
    return clusterMemberCprs.find(m =>
      memberMatchesSelection({ id: m.reflectorId, name: m.reflectorName }, { id: selectedClusterReflector, name })
    );
  }, [selectedEntity, selectedClusterReflector, clusterMemberCprs, getClusterReflectors]);

  // The selected member *when it is down*, or null — modelling absence directly so the render
  // branch narrows without a non-null assertion.
  const downSelectedMember = selectedMember && !isClusterMemberUp(selectedMember) ? selectedMember : null;

  // Every member of the selected cluster is down: Table and Topology both show the down state
  // instead of their generic empty states.
  const allClusterMembersDown =
    selectedEntity?.type === 'cluster' && clusterMemberCprs.length > 0 && upMembers.length === 0;

  // The healthy member the overlay CTA jumps to: `pickPreferredClusterMember` already prefers an
  // up PRIMARY, then an up SECONDARY, then any up member (CTX-71), and returns a SIDEBAR id ready
  // for `handleClusterReflectorChange`. Null when no other member is up — the CTA is then hidden.
  const healthyMemberTarget = useMemo(() => {
    if (selectedEntity?.type !== 'cluster' || upMembers.length === 0) {
      return null;
    }
    const reflectors = getClusterReflectors(selectedEntity.id);
    const targetId = pickPreferredClusterMember(reflectors, upMembers);
    if (!targetId || targetId === selectedClusterReflector) {
      return null;
    }
    const reflector = reflectors.find(r => r.id === targetId);
    const member = upMembers.find(m =>
      memberMatchesSelection(
        { id: m.reflectorId, name: m.reflectorName },
        { id: targetId, name: reflector?.display_name ?? null }
      )
    );
    return { id: targetId, role: member?.role ?? null, name: member?.reflectorName || reflector?.display_name || targetId };
  }, [selectedEntity, upMembers, getClusterReflectors, selectedClusterReflector]);

  // Rows for the TOPOLOGY graph + its metrics: scoped to the SELECTED reflector only (CTX-98).
  // Selecting PRIMARY vs SECONDARY shows that member's own routes/counts instead of the pooled
  // cluster union. In reflector (non-cluster) mode this is just the entity's rows.
  //
  // Reads the endpoint's rows when they are there, so Topology and Table can never disagree about
  // which routes exist. A DOWN member yields nothing here on purpose: its rows are unverifiable,
  // so they must not reach the metrics, the prefix counters or the CTX-106 comparison. They reach
  // the canvas — blurred, behind the overlay — through `topologyGraphRows` alone. On the document
  // path that fell out of `allMemberRows` already excluding down members; on this one it has to be
  // said out loud, because the endpoint hands back every member.
  const selectedReflectorRows = useMemo(() => {
    const source = serverEntityRows ?? allMemberRows;
    if (selectedEntity?.type !== 'cluster') {
      return source;
    }
    if (!selectedTopologyReflectorId || downSelectedMember) {
      return [];
    }
    return source.filter(r => r.reflectorId === selectedTopologyReflectorId);
  }, [selectedEntity, selectedTopologyReflectorId, downSelectedMember, serverEntityRows, allMemberRows]);

  // Filtered rows for the active view drive both the surface and the metrics, so
  // topology / table / metrics always reflect the same active filter set.
  const filteredTopologyRows = useMemo(
    () => applyActiveFilters(selectedReflectorRows),
    [applyActiveFilters, selectedReflectorRows]
  );
  const filteredTableRows = useMemo(
    () => applyActiveFilters(tableRows),
    [applyActiveFilters, tableRows]
  );

  // Rows actually DRAWN on the canvas. Normally the verified rows above — but when the selected
  // member is down while still holding a document, we deliberately draw its stale graph so the
  // overlay has real context behind it instead of an empty canvas. Those rows are for the picture
  // only: they are blurred, non-interactive, and never reach `metrics`, the prefix counters, the
  // table or the CTX-106 comparison, all of which keep deriving from `selectedReflectorRows`.
  const topologyGraphRows = useMemo(() => {
    if (!downSelectedMember || !selectedEntity) {
      return selectedReflectorRows;
    }
    // The endpoint reads every member's document regardless of availability, so a down member's
    // last-known rows come back with the rest — no second derivation needed for this case.
    if (serverEntityRows) {
      return serverEntityRows.filter(r => r.reflectorId === downSelectedMember.reflectorId);
    }
    return buildRouteRows({
      cpr: downSelectedMember.cpr,
      direction,
      reflectorId: downSelectedMember.reflectorId,
      reflectorName: downSelectedMember.reflectorName,
      reflectorRole: downSelectedMember.role,
      clusterId: selectedEntity.id,
      clusterName: selectedEntity.display_name,
      neighborAsByIp
    });
  }, [downSelectedMember, selectedEntity, selectedReflectorRows, serverEntityRows, direction, neighborAsByIp]);

  const filteredTopologyGraphRows = useMemo(
    () => applyActiveFilters(topologyGraphRows),
    [applyActiveFilters, topologyGraphRows]
  );
  const activeFilteredRows = viewMode === 'table' ? filteredTableRows : filteredTopologyRows;

  // The count line under the search bar shows distinct PREFIXES (`prefix|family`), not raw route
  // rows — a single prefix can span multiple route-tables/paths, so the row count overstated it.
  const matchedPrefixCount = useMemo(
    () => countDistinctPrefixes(activeFilteredRows),
    [activeFilteredRows]
  );
  /**
   * The document-derived counterpart of `selectedReflectorRows`, kept only as the denominator's
   * baseline (below). Same scoping rule, unfiltered.
   */
  const documentSelectedReflectorRows = useMemo(() => {
    if (selectedEntity?.type !== 'cluster') {
      return allMemberRows;
    }
    if (!selectedTopologyReflectorId) {
      return [];
    }
    return allMemberRows.filter(r => r.reflectorId === selectedTopologyReflectorId);
  }, [selectedEntity, selectedTopologyReflectorId, allMemberRows]);

  const totalPrefixCount = useMemo(() => {
    // The denominator has to be the UNFILTERED set, so it can never read the rendered rows: the
    // endpoint's arrive already narrowed, and the line would report "21 of 21" under any active
    // filter. The document is still fetched for cluster liveness and entity status, so its rows
    // are the honest baseline for both surfaces — and the day that fetch goes away, this is the
    // one thing that needs a new source, not the rendering.
    const rows = viewMode === 'table' ? allMemberRows : documentSelectedReflectorRows;
    return countDistinctPrefixes(rows);
  }, [viewMode, allMemberRows, documentSelectedReflectorRows]);

  const metrics = useMemo(() => computeRouteMetrics(activeFilteredRows), [activeFilteredRows]);

  // The ClusterSelector floats over the top-centre of the canvas.
  const showClusterSelector = selectedEntity?.type === 'cluster' && viewMode === 'topology';

  // "Reflector up, but nothing is peering", surfaced as the compact `0 Established` counter in
  // MetricsDisplay — deliberately a counter and not a canvas banner, which read as far too
  // invasive for a standing condition.
  //
  // Neighbors come from the ROSTER, not the drawn rows: a down session exchanges no prefixes, so
  // deriving the peer list from routes made the state unreachable in exactly the case it exists
  // for — every session down means zero rows, which read as "no neighbors at all". Gated on a
  // reporting reflector inside the helper, which keeps it mutually exclusive with reflector-down.
  const bgpSessionState = useMemo(
    () =>
      deriveBgpSessionState({
        neighbors: rosterSessionNeighbors(neighborData),
        members: clusterMemberCprs,
        reflectorHasData: entityStatusInfo?.hasData ?? false,
        lastUpdate: entityStatusInfo?.lastUpdate ?? null
      }),
    [neighborData, clusterMemberCprs, entityStatusInfo]
  );

  // Peers the reflector reports as down that carry no routes. Without these the topology drew only
  // what the route payload mentioned, so a down peer was indistinguishable from one that had never
  // been configured. Derived against the rows actually DRAWN so a peer already on the canvas (down
  // but still holding stale prefixes) is never duplicated.
  const downNeighbors = useMemo(
    () => deriveDownNeighborsWithoutRoutes(neighborData, filteredTopologyGraphRows.map(row => row.neighborIp)),
    [neighborData, filteredTopologyGraphRows]
  );

  // Nothing is exchanging routes at all, yet the reflector reports peers that are down. Draw those
  // peers rather than the generic "no routes" empty state, which hid their existence entirely.
  // Keyed on the UNFILTERED rows on purpose: when routes exist but a filter excluded them, the
  // empty state is the honest answer and must stay.
  const showDownNeighborsOnEmptyCanvas = selectedReflectorRows.length === 0 && downNeighbors.length > 0;

  // Rows announced by the OTHER cluster member(s) (not the selected reflector), passed through the
  // SAME active filters as the drawn rows so the inconsistency comparison is like-for-like (CTX-106).
  // Down members are already absent from `allMemberRows` (CTX-125), so a yellow inconsistency badge
  // can never be raised by comparing against a member's stale, unverifiable data.
  const filteredOtherMemberRows = useMemo(() => {
    if (selectedEntity?.type !== 'cluster' || !selectedTopologyReflectorId) {
      return [] as RouteRow[];
    }
    const source = serverEntityRows ? serverEntityRows.filter(rowMemberIsUp) : allMemberRows;
    const others = source.filter(r => r.reflectorId !== selectedTopologyReflectorId);
    return applyActiveFilters(others);
  }, [serverEntityRows, rowMemberIsUp, allMemberRows, selectedEntity, selectedTopologyReflectorId, applyActiveFilters]);

  // Destinations (route table inbound / subnet outbound) where the selected reflector and the other
  // member announce a DIFFERENT number of distinct prefixes → flagged inconsistent, which turns the
  // edge's `×N` count badge yellow (CTX-106). Empty outside cluster mode.
  const inconsistentRouteTables = useMemo(() => {
    const result = new Set<string>();
    if (selectedEntity?.type !== 'cluster' || !selectedTopologyReflectorId) {
      return result;
    }
    const keyOf = (r: RouteRow) => (direction === 'OUTBOUND' ? (r.subnetId || r.routeTableId) : r.routeTableId);
    const countByDest = (list: RouteRow[]) => {
      const map = new Map<string, Set<string>>();
      for (const r of list) {
        if (r.withdrawn) continue;
        const key = keyOf(r);
        if (!key) continue;
        let set = map.get(key);
        if (!set) {
          set = new Set<string>();
          map.set(key, set);
        }
        set.add(`${r.prefix}|${r.family}`);
      }
      return map;
    };
    const selected = countByDest(filteredTopologyRows);
    const other = countByDest(filteredOtherMemberRows);
    const dests = new Set<string>([...selected.keys(), ...other.keys()]);
    dests.forEach(dest => {
      if ((selected.get(dest)?.size ?? 0) !== (other.get(dest)?.size ?? 0)) {
        result.add(dest);
      }
    });
    return result;
  }, [selectedEntity, selectedTopologyReflectorId, filteredTopologyRows, filteredOtherMemberRows, direction]);

  // Reflector options for the table filter (cluster mode only). Down members are omitted —
  // they contribute no rows, so offering them would only ever yield an empty table.
  const reflectorFilterOptions = useMemo(
    () => upMembers.map(m => ({ id: m.reflectorId, name: m.reflectorName || m.reflectorId })),
    [upMembers]
  );

  // Rows for the docked neighbor-detail panel (respect the active filters).
  const neighborPanelRows = useMemo(
    () => (selectedNeighborIp ? filteredTopologyRows.filter(r => r.neighborIp === selectedNeighborIp) : []),
    [selectedNeighborIp, filteredTopologyRows]
  );

  // Close the detail panel when the context (entity / direction / member) changes.
  useEffect(() => {
    setSelectedNeighborIp(null);
  }, [selectedEntity?.id, selectedEntity?.type, direction, selectedClusterReflector]);

  // Generate topology graph from the filtered single-member rows.
  const generateGraph = useCallback(() => {
    // Down peers are drawable on their own: with every session down there are no rows at all, and
    // clearing the canvas there is what made them invisible.
    if (filteredTopologyGraphRows.length === 0 && downNeighbors.length === 0) {
      setNodes([]);
      setEdges([]);
      return;
    }

    const { nodes: newNodes, edges: filteredEdges } = generateGraphNodesAndEdges({
      rows: filteredTopologyGraphRows,
      direction,
      selectedEntity,
      // Rows are scoped to the selected reflector (CTX-98). In cluster mode, pass the set of
      // destinations whose prefix count differs from the other member so those edges get a yellow
      // count badge (CTX-106). Reflector mode → no comparison, best/alternate colouring only.
      inconsistentRouteTables: selectedEntity?.type === 'cluster' ? inconsistentRouteTables : undefined,
      downNeighbors
    });

    setNodes(newNodes);
    setEdges(filteredEdges);

    // Use requestAnimationFrame to prevent ResizeObserver issues
    requestAnimationFrame(() => {
      fitView({
        padding: 0.3,
        includeHiddenNodes: false,
        minZoom: 0.2,
        maxZoom: 1.2
      });
    });
  }, [filteredTopologyGraphRows, direction, fitView, selectedEntity, inconsistentRouteTables, downNeighbors, setNodes, setEdges]);

  // Regenerate graph when the filtered topology rows change (only while in topology view).
  useEffect(() => {
    if (viewMode !== 'topology') return;
    const timeoutId = setTimeout(() => {
      generateGraph();
    }, 50); // Very short debounce to prevent rapid updates

    return () => clearTimeout(timeoutId);
  }, [filteredTopologyRows, viewMode, generateGraph]);

  // Open the docked detail panel for the clicked neighbor.
  const handleBgpNeighborClick = useCallback((data: BgpNeighborData) => {
    setSelectedNeighborIp(data.neighborIp);
  }, []);

  const handlePrefixClick = useCallback((prefixData: any, neighborIp: string) => {
    const neighborInfo = neighborIp ? neighborData[neighborIp] : undefined;
    setIpv4InfoModal({ isOpen: true, prefixData, neighborIp, neighborInfo });
  }, [neighborData]);

  const handleVpcClick = useCallback((data: VpcData) => {
    setVpcModal({ isOpen: true, data });
  }, []);

  const handleRouteTableClick = useCallback((routeTableData: any, vpcId: string) => {
    setRouteTableModal({ isOpen: true, routeTableData, vpcId });
  }, []);

  // Handle node clicks from ReactFlow
  const handleNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    if (node.type === 'neighbor' && node.data) {
      // A down peer has no routes, so the detail panel would open empty — matching the card,
      // which renders no CTA for it.
      if (node.data.isDown) return;
      handleBgpNeighborClick(node.data);
    } else if (node.type === 'vpc' && node.data) {
      handleVpcClick(node.data);
    } else if (node.type === 'cloud' && node.data) {
      // Handle cloud container click
      const data = node.data as CloudData;
      if (data.vpcs.length > 0) {
        const firstVpc = data.vpcs[0];
        const routeTables = data.allData
          .filter((entry: any) => entry.vpc === firstVpc)
          .reduce((acc: any, entry: any) => {
            const existing = acc.find((rt: any) => rt.route_table_id === entry.route_table_id);
            if (existing) {
              existing.prefixes.push(entry);
            } else {
              acc.push({
                route_table_id: entry.route_table_id,
                prefixes: [entry]
              });
            }
            return acc;
          }, []);
        handleVpcClick({ vpcId: firstVpc, routeTables });
      }
    }
  }, [handleBgpNeighborClick, handleVpcClick]);

  // Create node types with handlers - memoize to prevent React Flow warnings
  const nodeTypes = useMemo(() => {
    return createNodeTypes(
      handleBgpNeighborClick,
      handleVpcClick,
      handleRouteTableClick,
      direction === 'OUTBOUND',
      neighborData
    );
  }, [handleBgpNeighborClick, handleVpcClick, handleRouteTableClick, direction, neighborData]);

  // Custom edge type: draws a bezier with its endpoints shifted by `data.offset` so the pink
  // (shared) and blue (unique) parallel edges between the same handles stay visibly separate.
  const edgeTypes = useMemo(() => ({ offsetBezier: OffsetBezierEdge }), []);

  return (
    <>
    <style>{bgpVisibilityStyles}</style>
      <div className="flex h-full overflow-hidden">
        {/* Cloud Inventory Sidebar - collapsible (hide to give the topology more room) */}
        <div
          className={`bg-semantic-bg-primary border-r border-semantic-border-secondary flex-shrink-0 relative overflow-hidden transition-[width] duration-200 ${leftSidebarCollapsed ? 'w-0 border-r-0' : 'w-[340px]'}`}
          style={{
            height: '90vh',
            maxHeight: '90vh'
          }}
        >
          {!leftSidebarCollapsed && (
            <CloudInventorySidebar
              cloudInventory={cloudInventory}
              onEntitySelect={setSelectedEntity}
              selectedEntity={selectedEntity}
              isLoading={isFetching && !cloudInventory}
            />
          )}
        </div>

        {/* Collapse handle straddling the inventory/canvas divider. It is the only inventory
            collapse control, so it stays mounted (flipping direction) while the sidebar is
            hidden — the zero-width wrapper lets the circle overhang both sides of the border. */}
        <div className="relative z-20 w-0 flex-shrink-0">
          <button
            type="button"
            onClick={() => setLeftSidebarCollapsed(!leftSidebarCollapsed)}
            title={leftSidebarCollapsed ? 'Show inventory' : 'Hide inventory'}
            aria-label={leftSidebarCollapsed ? 'Show cloud inventory' : 'Hide cloud inventory'}
            aria-expanded={!leftSidebarCollapsed}
            /* Straddles the border while the sidebar is open; nudged fully inside once it is
               collapsed, since at that point x=0 is the pane's edge and half the circle would
               be clipped by the row's overflow-hidden. */
            className={`absolute top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full border border-semantic-border-secondary bg-semantic-bg-primary text-semantic-text-secondary shadow-sm transition-colors hover:bg-semantic-state-active hover:text-semantic-text-primary ${
              leftSidebarCollapsed ? 'translate-x-1' : '-translate-x-1/2'
            }`}
          >
            {leftSidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        </div>

        {/* Main Graph Area */}
        <div className="flex flex-col flex-1 overflow-hidden min-h-0 min-w-0">
          {/* Rendered up into the SideNav title row, on the same line as the "BGP Visibility"
              heading, so the controls cost the canvas no vertical space at all. */}
          <SideNavTitlePortal>
            <VisibilityToolbar
              direction={direction}
              onDirectionChange={setDirection}
              bestOnly={bestOnly}
              onBestOnlyToggle={() => setBestOnly(!bestOnly)}
              onRefresh={async () => {
                await loadCprData();
                await fetchNeighborData();
              }}
              loading={loading}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              metrics={metrics}
              lastUpdate={lastUpdate}
              allSessionsDown={bgpSessionState.allSessionsDown}
              filter={
                selectedEntity
                  ? {
                      onChange: setRouteFilters,
                      reflectorOptions: reflectorFilterOptions,
                      includeReflector: viewMode === 'table' && selectedEntity.type === 'cluster',
                      matchedCount: matchedPrefixCount,
                      totalCount: totalPrefixCount
                    }
                  : null
              }
            />
          </SideNavTitlePortal>

          {/* Graph Content - Full Bleed (with optional docked neighbor-detail panel) */}
          <div className="flex-1 flex overflow-hidden min-h-0 min-w-0">
          <div className="flex-1 relative overflow-hidden min-h-0 min-w-0">
            {showClusterSelector && (
              <ClusterSelector
                reflectors={getClusterReflectors(selectedEntity.id)}
                selectedReflector={selectedClusterReflector}
                onReflectorChange={handleClusterReflectorChange}
                members={clusterMemberCprs}
              />
            )}
          <LicenseGate gate={licenseGate} fullBleed>
            {error ? (
              <div className="flex items-center justify-center h-full p-6">
                <ErrorStateView
                  title="Error loading data"
                  message={error}
                  onRetry={() => {
                    setError(null);
                    if (!initialDataLoaded) fetchCloudInventory();
                    else if (selectedEntity) loadCprData();
                  }}
                />
              </div>
          ) : !selectedEntity && initialDataLoaded ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Server size={48} className="text-semantic-text-secondary mx-auto mb-4" />
                <p className="text-semantic-text-secondary font-semibold mb-2">No device selected</p>
                <p className="text-semantic-text-secondary text-sm">Select any Reflector or Cluster from the Cloud Inventory</p>
              </div>
            </div>
          ) : viewMode === 'table' ? (
            <BgpTableView
              filteredRows={filteredTableRows}
              allRows={tableRows}
              direction={direction}
              loading={loading}
              isCluster={selectedEntity?.type === 'cluster'}
              downMembers={downMembers}
              allMembersDown={allClusterMembersDown}
              formatMemberLabel={m => clusterMemberLabel(m.role, m.reflectorName)}
            />
          ) : !licenseGate && downSelectedMember ? (
            /* The selected member is not reporting. Its last-known topology is still DRAWN — so the
               operator can see the shape of what was there — but blurred, dimmed and behind a
               blocking scrim, so it reads as unverifiable context rather than live data. When the
               member carries no document at all there is simply nothing behind the card (CTX-125). */
            <div className="relative h-full w-full">
              <div className="pointer-events-none absolute inset-0 select-none opacity-60 blur-[3px]" aria-hidden>
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  nodeTypes={nodeTypes}
                  edgeTypes={edgeTypes}
                  nodesDraggable={false}
                  elementsSelectable={false}
                  nodesConnectable={false}
                  panOnDrag={false}
                  zoomOnScroll={false}
                  zoomOnPinch={false}
                  zoomOnDoubleClick={false}
                  fitView
                  fitViewOptions={{ padding: 0.2, includeHiddenNodes: false, minZoom: 0.1, maxZoom: 2 }}
                  defaultEdgeOptions={{ type: 'default' }}
                  proOptions={{ hideAttribution: true }}
                />
              </div>
              <ReflectorDownOverlay
                memberLabel={clusterMemberLabel(downSelectedMember.role, downSelectedMember.reflectorName)}
                lastUpdate={entityStatusInfo?.lastUpdate}
                healthyMemberLabel={healthyMemberTarget ? viewMemberCtaLabel(healthyMemberTarget.role, healthyMemberTarget.name) : null}
                onViewHealthy={healthyMemberTarget ? () => handleClusterReflectorChange(healthyMemberTarget.id) : null}
              />
            </div>
          ) : !currentCpr ? (
            <div className="h-full w-full relative">
              {loading ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 z-[100]" role="status" aria-busy="true">
                  <h2 className="text-xl font-semibold mb-4 text-white">Loading...</h2>
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-500" />
                </div>
              ) : null}
              {!loading && entityStatusInfo && !entityStatusInfo.hasData ? (
                /* Single-reflector (and roleless) down state — the same card the cluster branch
                   above uses, without a CTA since there is no sibling member to fall back to. */
                <ReflectorDownOverlay
                  memberLabel={entityStatusInfo.name || selectedEntity?.display_name || ''}
                  lastUpdate={entityStatusInfo.lastUpdate}
                />
              ) : !loading && (
              <div className="flex items-center justify-center h-full p-6">
                <div className="text-center">
                  <p className="text-semantic-text-brand font-semibold mb-2">Waiting for data...</p>
                  <p className="text-semantic-text-brand text-sm">Connected to {selectedEntity?.display_name} - waiting for data</p>
                </div>
              </div>
              )}
            </div>
          ) : loading ? (
            <div className="h-full w-full relative">
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 z-[100]" role="status" aria-busy="true">
                <h2 className="text-xl font-semibold mb-4 text-white">Loading...</h2>
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-500" />
              </div>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onNodeClick={handleNodeClick}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                nodesDraggable={false}
                elementsSelectable={false}
                nodesConnectable={false}
                panOnDrag={true}
                zoomOnScroll={true}
                zoomOnPinch={true}
                fitView
                fitViewOptions={{ padding: 0.2, includeHiddenNodes: false, minZoom: 0.1, maxZoom: 2 }}
                defaultEdgeOptions={{ type: 'default' }}
                proOptions={{ hideAttribution: true }}
              >
                <GraphLegend cluster={selectedEntity?.type === 'cluster'} inconsistentCount={inconsistentRouteTables.size} />
              </ReactFlow>
            </div>
          ) : filteredTopologyRows.length === 0 && !showDownNeighborsOnEmptyCanvas ? (
            <div className="flex items-center justify-center h-full">
              <EmptyStateView
                title="No routes for current direction"
                description={`No ${direction.toLowerCase()} routes found for the selected filters`}
              />
            </div>
          ) : (
              <div className="absolute inset-0 w-full h-full">
                {loading && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 z-[100]" role="status" aria-busy="true">
                    <h2 className="text-xl font-semibold mb-4 text-white">Loading...</h2>
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-500" />
                  </div>
                )}
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onNodeClick={handleNodeClick}
                  nodeTypes={nodeTypes}
                  edgeTypes={edgeTypes}
                  nodesDraggable={false}
                  elementsSelectable={false}
                  nodesConnectable={false}
                  panOnDrag={true}
                  zoomOnScroll={true}
                  zoomOnPinch={true}
                  fitView
                  fitViewOptions={{ padding: 0.2, includeHiddenNodes: false, minZoom: 0.1, maxZoom: 2 }}
                  defaultEdgeOptions={{ type: 'default' }}
                  proOptions={{ hideAttribution: true }}
                >

                  <GraphLegend cluster={selectedEntity?.type === 'cluster'} inconsistentCount={inconsistentRouteTables.size} />
                </ReactFlow>
              </div>
            )}
          </LicenseGate>
        </div>
          {viewMode === 'topology' && selectedNeighborIp && (
            <NeighborDetailPanel
              neighborIp={selectedNeighborIp}
              rows={neighborPanelRows}
              direction={direction}
              neighborInfo={neighborData[selectedNeighborIp]}
              onClose={() => setSelectedNeighborIp(null)}
              onPrefixClick={handlePrefixClick}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      <IPv4InfoModal
        isOpen={ipv4InfoModal.isOpen}
        onClose={() => setIpv4InfoModal({ isOpen: false, prefixData: null, neighborIp: null, neighborInfo: undefined })}
        prefixData={ipv4InfoModal.prefixData}
        neighborIp={ipv4InfoModal.neighborIp}
        neighborInfo={ipv4InfoModal.neighborInfo}
      />
      <VpcModal
        isOpen={vpcModal.isOpen}
        onClose={() => setVpcModal({ isOpen: false, data: null })}
        data={vpcModal.data}
      />
      <RouteTableModal
        isOpen={routeTableModal.isOpen}
        onClose={() => setRouteTableModal({ isOpen: false, routeTableData: null, vpcId: null })}
        routeTableData={routeTableModal.routeTableData}
        vpcId={routeTableModal.vpcId}
        direction={direction}
      />
    </div>
    </>
  );
};

const BgpVisibilityPageWrapper: React.FC = () => {
  return (
    <ReactFlowProvider>
      <BgpVisibilityPage />
    </ReactFlowProvider>
  );
};

export default BgpVisibilityPageWrapper;